import { Component, Input } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { ViewManager } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';

/**
 * Compose Email cell.
 *
 * Reads an array context variable (typically the output of a Multi-Select cell),
 * pulls a chosen email field from each item, de-duplicates and cleans the list,
 * and opens the user's default mail client with those addresses pre-filled in the
 * To / Cc / Bcc field via a `mailto:` link. Optional subject and body are included.
 */
@Component({
  selector: 'bhub-email-compose-cell',
  standalone: false,
  templateUrl: './email-compose-cell.component.html',
  styleUrl: './email-compose-cell.component.scss'
})
export class EmailComposeCellComponent extends BaseCellComponent {
  @Input() viewManager: ViewManager | null = null;

  recipients: string[] = [];

  protected initializeCell(): void {
    this.recomputeRecipients();
  }

  protected onCellOrDataChange(): void {
    this.recomputeRecipients();
  }

  protected onCellUpdate(): void {
    this.recomputeRecipients();
  }

  protected onDataSet(_data: any): void {
    this.recomputeRecipients();
  }

  reloadData(): void {
    this.recomputeRecipients();
  }

  // --- Configuration getters ---
  get buttonText(): string {
    return this.getConfigValue('emailComposeButtonText', 'Compose Email') || 'Compose Email';
  }

  get buttonColor(): 'primary' | 'accent' | 'warn' {
    return this.getConfigValue('emailComposeButtonColor', 'primary') as 'primary' | 'accent' | 'warn';
  }

  get showCount(): boolean {
    return this.getConfigValue<boolean>('emailComposeShowCount', true) !== false;
  }

  private get contextVariable(): string {
    return this.getConfigValue('emailComposeContextVariable', '');
  }

  private get emailField(): string {
    return this.getConfigValue('emailComposeEmailField', '');
  }

  private get recipientMode(): 'to' | 'cc' | 'bcc' {
    return this.getConfigValue('emailComposeRecipientMode', 'to') as 'to' | 'cc' | 'bcc';
  }

  private get subject(): string {
    return this.getConfigValue('emailComposeSubject', '') || '';
  }

  private get body(): string {
    return this.getConfigValue('emailComposeBody', '') || '';
  }

  get recipientCount(): number {
    return this.recipients.length;
  }

  /** Disabled when there are no valid recipients (except in the builder preview). */
  get isDisabled(): boolean {
    return !this.isEditMode && this.recipientCount === 0;
  }

  get emptyMessage(): string {
    if (!this.contextVariable || !this.emailField) {
      return 'Not configured';
    }
    return 'No recipients selected';
  }

  /** Comma-joined preview of recipients for the button tooltip. */
  get recipientsTooltip(): string {
    if (this.recipientCount === 0) return this.emptyMessage;
    return this.recipients.join(', ');
  }

  private recomputeRecipients(): void {
    this.recipients = [];

    const contextVariable = this.contextVariable;
    const emailField = this.emailField;
    if (!contextVariable || !emailField || !this.viewManager?.contextData) {
      return;
    }

    const source = this.viewManager.contextData[contextVariable];
    if (!Array.isArray(source)) {
      return;
    }

    const seen = new Set<string>();
    for (const item of source) {
      if (!item || typeof item !== 'object') continue;
      const raw = item[emailField];
      if (raw === null || raw === undefined) continue;

      const email = String(raw).trim();
      if (!this.isValidEmail(email)) continue;

      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      this.recipients.push(email);
    }
  }

  /** Lightweight validity check — enough to keep blanks/garbage out of the mailto. */
  private isValidEmail(value: string): boolean {
    if (!value || /\s/.test(value)) return false;
    const at = value.indexOf('@');
    return at > 0 && at < value.length - 1 && value.indexOf('.', at) > at;
  }

  private buildMailto(): string {
    const addressList = this.recipients.map(e => encodeURIComponent(e)).join(',');
    const mode = this.recipientMode;

    let base = 'mailto:';
    const params: string[] = [];

    if (mode === 'to') {
      base += addressList;
    } else {
      params.push(`${mode}=${addressList}`);
    }

    if (this.subject) params.push(`subject=${encodeURIComponent(this.subject)}`);
    if (this.body) params.push(`body=${encodeURIComponent(this.body)}`);

    return params.length > 0 ? `${base}?${params.join('&')}` : base;
  }

  onCompose(): void {
    if (this.isEditMode || this.recipientCount === 0) return;
    // Opening a mailto: navigates the OS to the default mail client without
    // leaving the SPA. Assigning location.href is the most broadly compatible way.
    window.location.href = this.buildMailto();
  }
}
